import React, {
  useMemo, useCallback, useEffect, useRef
} from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import NotificationsIcon from '@material-ui/icons/Notifications';
import HelpIcon from '@material-ui/icons/Help';


import {
  loadHelpList, loadHelpCategories, loadHelpLanguages,
  readHelpNews, changeTabIndex
} from 'src/store/actions/helpActions';

import { selectHelpNewsList, selectHelpHelpItems } from 'src/store/selectors/help';
import { selectLocale } from 'src/store/selectors/translationSelectors';
import { selectIsSuper, selectIsManagerMode } from 'src/store/selectors/userInfo';
import { DO_NOTHING } from 'src/helpers/FPUtils';
import BarButton from './BarButton';

import '../styles/index.less';

function HeaderBar({
  pathname, changeEnabled, newsList, helpItems, lang, managerMode
}) {
  const dispatch = useDispatch();
  const langFetching = useRef('');

  useEffect(
    () => {
      dispatch(loadHelpCategories());
      dispatch(loadHelpLanguages());
    },
    [dispatch]
  );

  useEffect(
    () => {
      if (!helpItems[lang] && lang !== langFetching.current) {
        dispatch(loadHelpList(lang, changeEnabled)).catch(DO_NOTHING);
        langFetching.current = lang;
      }
    },
    [changeEnabled, lang, helpItems, dispatch]
  );

  const helpList = useMemo(
    () => (helpItems[lang]
      ? helpItems[lang].reduce((counter, item) => {
        if (item.pages.indexOf(pathname) >= 0) {
          counter.push(item);
        }
        if (item.children) {
          counter = counter.concat(item.children.filter(({ pages }) => pages.indexOf(pathname) >= 0));
        }
        return counter;
      }, [])
      : []),
    [helpItems, lang, pathname]
  );

  const seeAll = useCallback(
    () => {
      const allIds = newsList[lang].map(({ id }) => id);
      dispatch(readHelpNews(allIds, lang, changeEnabled));
    },
    [newsList, lang, dispatch, changeEnabled]
  );

  const handleChangeTabIndex = useCallback(
    (newIndex) => {
      dispatch(changeTabIndex(newIndex));
    },
    [dispatch]
  );

  return (
    <div className="help-header-container">
      <BarButton
        icon={<HelpIcon />}
        list={helpList}
        changeTab={handleChangeTabIndex}
        changeTabIndex={1}
      />
      {managerMode && (
        <BarButton
          icon={<NotificationsIcon />}
          newsCount={newsList[lang] ? newsList[lang].length : 0}
          bottomButtons
          list={newsList[lang] || []}
          seeAll={seeAll}
          changeTab={handleChangeTabIndex}
          changeTabIndex={3}
          readAll
        />
      )}
    </div>
  );
}

HeaderBar.propTypes = {
  pathname: PropTypes.string.isRequired,
  changeEnabled: PropTypes.bool,
  managerMode: PropTypes.bool,
  newsList: PropTypes.object.isRequired,
  helpItems: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired
};

HeaderBar.defaultProps = {
  changeEnabled: false,
  managerMode: true
};

const mapStateToProps = (state) => ({
  newsList: selectHelpNewsList(state),
  helpItems: selectHelpHelpItems(state),
  lang: selectLocale(state),
  changeEnabled: selectIsSuper(state),
  managerMode: selectIsManagerMode(state)
});

export default connect(mapStateToProps, null)(HeaderBar);
